import {
  afterNextRender,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  linkedSignal,
  model,
  signal,
  Signal,
  viewChild,
  WritableSignal
} from '@angular/core';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuPanel } from '@angular/material/menu';
import { ImportTasksFromTemplateComponent } from '@app/components/task/import-tasks-from-template/import-tasks-from-template.component';
import { TaskFiltersAdvancedComponent } from '@app/components/task/task-list-toolbar/task-filters-advanced/task-filters-advanced.component';
import { TaskFiltersService } from '@app/components/task/task-list-toolbar/task-filters.service';
import { TaskMobileViewSettingsComponent } from '@app/components/task/task-list-toolbar/task-mobile-view-settings/task-mobile-view-settings.component';
import { FilterCountBadgeDirective } from '@app/shared/directives/filter-count-badge.directive';
import { TaskScheduleActionsService } from '@app/shared/services/actions/task-schedule-actions.service';
import { TasksActionsService } from '@app/shared/services/actions/tasks-actions.service';
import { ActiveProjectService } from '@app/shared/services/active-project.service';
import { DeviceService } from '@app/shared/services/shared/device.service';
import { DialogService } from '@app/shared/services/shared/dialog.service';
import { ProjectTaskUserRole } from '@app/shared/types/enums/project-task-user-role.enum';
import { TaskStatus } from '@app/shared/types/enums/task-status.enum';
import { Project } from '@app/shared/types/models/project/project.model';
import { TaskFilters } from '@app/shared/types/models/task/task-filters.model';
import { TaskListViewMode } from '@app/shared/types/models/task/task-schedule.model';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';
import { UiDateRangePickerComponent } from '@app/shared/ui/date-range-picker/ui-date-range-picker.component';
import { UiInputComponent } from '@app/shared/ui/input/ui-input.component';
import { UiSelectComponent } from '@app/shared/ui/select/ui-select.component';
import { ProffeoUiSelectOption, proffeoUiSelectSelectionCount } from '@app/shared/ui/types/select.types';
import { TasksListActionsService } from '@app/views/company/projects/tasks/tasks-list-actions.service';
import { TranslatePipe } from '@ngx-translate/core';
import { isEqual } from 'lodash';

/**
 * @summary Compact task list toolbar: view toggle and table settings, quick filters, Filtry menu,
 * import/export actions.
 * @selector proffeo-task-list-toolbar
 */
@Component({
  selector: 'proffeo-task-list-toolbar',
  templateUrl: './task-list-toolbar.component.html',
  styleUrl: './task-list-toolbar.component.scss',
  host: {
    class: 'overflow-visible'
  },
  imports: [
    UiButtonComponent,
    UiDateRangePickerComponent,
    TaskFiltersAdvancedComponent,
    TaskMobileViewSettingsComponent,
    ImportTasksFromTemplateComponent,
    FilterCountBadgeDirective,
    TranslatePipe,
    UiInputComponent,
    UiSelectComponent,
    MatMenuModule,
    MatButtonToggleModule,
    MatIconModule
  ]
})
export class TaskListToolbarComponent {
  private readonly tasksListService: TasksListActionsService = inject(TasksListActionsService);
  private readonly tasksActionsService: TasksActionsService = inject(TasksActionsService);
  private readonly activeProjectService: ActiveProjectService = inject(ActiveProjectService);
  private readonly deviceService: DeviceService = inject(DeviceService);
  private readonly dialogService: DialogService = inject(DialogService);
  private readonly injector: Injector = inject(Injector);
  private readonly isMobileSearchFocused = signal(false);
  private readonly mobileSearch = viewChild<unknown, ElementRef<HTMLElement>>('mobileSearch', { read: ElementRef });

  protected readonly scheduleActions = inject(TaskScheduleActionsService, { optional: true });
  protected readonly taskFiltersState: TaskFiltersService = inject(TaskFiltersService);

  protected readonly searchPhrase = this.taskFiltersState.searchPhrase;
  protected readonly activeFiltersCount = this.taskFiltersState.activeFiltersCount;
  protected readonly advancedActiveFiltersCount = this.taskFiltersState.advancedActiveFiltersCount;
  protected readonly isMobile: Signal<boolean> = this.deviceService.isMobile;
  protected readonly isMobileSearchExpanded = computed((): boolean => this.isMobile() && this.isMobileSearchFocused());
  protected readonly activeProject: Signal<Project | undefined> = this.activeProjectService.activeProject;
  protected readonly showImportSection: WritableSignal<boolean> = signal(false);

  protected readonly filters: Signal<TaskFilters | null> = this.taskFiltersState.filters;
  protected readonly statusOptions: Signal<ProffeoUiSelectOption<TaskStatus>[]> =
    this.taskFiltersState.availableStatusOptions;
  protected readonly usersOptions: Signal<ProffeoUiSelectOption<string>[]> =
    this.taskFiltersState.availableUsersOptions;
  protected readonly myRolesOptions: Signal<ProffeoUiSelectOption<ProjectTaskUserRole>[]> = computed(() => [
    { value: ProjectTaskUserRole.USER, label: 'task-filters.myContractorTasks' },
    { value: ProjectTaskUserRole.INDUSTRY_ADMIN, label: 'task-filters.myIndustryTasks' },
    { value: ProjectTaskUserRole.GROUP_ADMIN, label: 'task-filters.myGroupTasks' }
  ]);

  protected readonly taskStatusSelection: WritableSignal<TaskStatus[]> = linkedSignal(
    () => this.filters()?.taskStatus ?? []
  );
  protected readonly userSelection: WritableSignal<string[] | null> = linkedSignal(() => {
    const filters = this.filters();
    if (filters?.noUsers) {
      return null;
    }
    return filters?.user ?? [];
  });
  protected readonly myRolesSelection: WritableSignal<ProjectTaskUserRole[]> = linkedSignal(() =>
    this.taskFiltersState.selectedMyRoles()
  );

  protected readonly taskStatusBadgeCount = computed((): number =>
    proffeoUiSelectSelectionCount(this.filters()?.taskStatus)
  );
  protected readonly userBadgeCount = computed((): number => proffeoUiSelectSelectionCount(this.userSelection(), true));
  protected readonly myRolesBadgeCount = computed((): number => this.taskFiltersState.myRolesActiveCount());
  protected readonly mobileFiltersBadgeCount = computed(
    (): number => this.advancedActiveFiltersCount() + this.myRolesBadgeCount()
  );
  protected readonly tableSettingsMenu = computed((): MatMenuPanel | null =>
    this.viewMode() !== 'cards' ? (this.columnManagerMenu() ?? null) : null
  );

  /** @summary Desktop list view mode (cards / table); two-way bound by the tasks view. */
  public readonly viewMode = model<TaskListViewMode>('table');
  /** @summary Table column-manager menu; its settings button shows only in table view. */
  public readonly columnManagerMenu = input<MatMenuPanel | null | undefined>(null);

  public constructor() {
    effect(() => {
      const next = this.taskStatusSelection();
      const current = this.filters()?.taskStatus ?? [];
      if (!isEqual(next, current)) {
        this.taskFiltersState.updateFiltersField('taskStatus', next);
      }
    });

    effect(() => {
      const value = this.userSelection();
      const filters = this.filters();

      if (value === null) {
        const currentEmpty = filters?.noUsers ?? false;
        const currentArray = filters?.user ?? [];
        if (!currentEmpty || currentArray.length > 0) {
          this.taskFiltersState.updateFiltersField('noUsers', true);
          this.taskFiltersState.updateFiltersField('user', []);
        }
        return;
      }

      const currentEmpty = filters?.noUsers ?? false;
      const currentArray = filters?.user ?? [];
      if (currentEmpty || !isEqual(value, currentArray)) {
        this.taskFiltersState.updateFiltersField('noUsers', false);
        this.taskFiltersState.updateFiltersField('user', value);
      }
    });

    effect(() => {
      const next = this.myRolesSelection();
      const current = this.taskFiltersState.selectedMyRoles();
      if (!isEqual(next, current)) {
        this.taskFiltersState.setMyRolesFilters(next);
      }
    });
  }

  protected onSearch(searchValue: string | null): void {
    this.taskFiltersState.setSearchPhrase(searchValue ?? null);
  }

  protected expandMobileSearch(): void {
    if (!this.isMobile()) {
      return;
    }

    this.isMobileSearchFocused.set(true);
    afterNextRender(() => this.mobileSearch()?.nativeElement.querySelector('input')?.focus(), {
      injector: this.injector
    });
  }

  protected collapseMobileSearch(): void {
    this.isMobileSearchFocused.set(false);
  }

  protected filtersCleared(): void {
    this.taskFiltersState.clearFilters();
  }

  protected startTaskCreation(): void {
    this.tasksListService.startTaskCreation();
  }

  protected openAdvancedFiltersMobile(): void {
    this.dialogService.openTaskAdvancedFiltersDialog(this.injector);
  }

  protected toggleImportSection(): void {
    this.showImportSection.update(isVisible => !isVisible);
  }

  protected exportTasks(): void {
    this.tasksActionsService.exportTasks({
      orderBy: this.taskFiltersState.orderBy(),
      order: this.taskFiltersState.order(),
      taskFiltersId: this.taskFiltersState.taskFiltersId(),
      filters: this.taskFiltersState.filters(),
      searchPhrase: this.taskFiltersState.searchPhrase()
    });
  }
}
